import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '../api/client'
import { getDesk, getIdeas, getSearchResults, notOurHobby, refreshIdeas, type Idea, type IdeaReason, type SearchPiece } from './api'
import { chipClass, etDate, etTime, stageLabel } from './labels'
import { useCanDraft, useEditorialTitle } from './titleContext'
import { IDEAS_EMPTY, IDEAS_UNAVAILABLE, POLL_MS, SETTLE_NOTE, filterCounts, hasSearchDemand, parseShow, resultsHeadline, refreshMessage, shouldPoll, showResultsPanel, type RefreshState } from './ideas'
import { pieceHref, startIdea, startLink } from './links'
import { RefreshPagesSection } from './RefreshPagesSection'
import { deskPollMs } from './desk'
import { describeError } from '../lib/describeError'

const focus = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-glow'
const card = 'rounded-xl border border-ink-500 bg-ink-700'
const field = `min-h-11 w-full rounded-lg border border-ink-400 bg-ink-800 px-3 font-ui text-[14px] text-paper placeholder:text-paper-faint ${focus}`
const btn = `inline-flex min-h-10 items-center justify-center whitespace-nowrap rounded-lg border border-ink-400 px-4 font-ui text-[14px] font-semibold text-paper hover:bg-ink-600 ${focus}`
const btnPrimary = `inline-flex min-h-10 items-center justify-center whitespace-nowrap rounded-lg border border-amber-glow bg-amber-glow px-4 font-ui text-[14px] font-semibold text-ink-900 hover:opacity-90 ${focus}`
const chip = 'inline-flex h-6 items-center rounded-full bg-ink-600 px-2.5 font-ui text-[12px] font-medium text-paper-dim'

function Reason({ r }: { r: IdeaReason }) {
  // Evidence is plain text in a title attribute; React never treats it as HTML.
  const title = r.evidence ?? undefined
  if (r.url) {
    return <a href={r.url} target="_blank" rel="noopener noreferrer" title={title} className={`${chip} no-underline hover:text-paper ${focus}`}>{r.label}</a>
  }
  return <span title={title} className={chip}>{r.label}</span>
}

/** The search keyword on a search-demand card: its top search_demand evidence, else the card's label. */
function phraseOf(idea: Idea): string {
  return idea.label
}

function IdeaCard({ idea, path, onHide, hiding }: { idea: Idea; path: string; onHide: (idea: Idea) => void; hiding: boolean }) {
  const canDraft = useCanDraft()
  return (
    <li className={`${card} flex flex-wrap items-center gap-x-[22px] gap-y-3.5 px-[18px] py-4`}>
      <span className="w-[34px] flex-none font-serif text-[24px] text-paper-faint">{idea.rank}</span>
      <div className="flex min-w-0 flex-[1_1_380px] flex-col gap-2">
        <span className="font-serif text-[21px] font-medium leading-tight text-paper">{idea.label}</span>
        {idea.ask && <span className="font-ui text-[14px] text-paper-dim">Readers ask: {idea.ask}</span>}
        <span className="flex flex-wrap gap-1.5">
          {idea.reasons.map((r) => <Reason key={r.key} r={r} />)}
        </span>
      </div>
      <div className="flex flex-[0_1_180px] flex-col gap-1.5">
        <span className="font-ui text-[12px] font-semibold text-paper-faint">Reader interest</span>
        <span className="flex gap-1" role="img" aria-label={idea.strength_label}>
          {[1, 2, 3, 4].map((n) => (
            <span key={n} className={`h-2 w-[22px] rounded-sm ${n <= idea.strength ? 'bg-amber-glow' : 'bg-ink-500'}`} />
          ))}
        </span>
        <span className="font-ui text-[12px] text-paper-dim">{idea.strength_label} · {idea.archive}</span>
      </div>
      <div className="flex flex-none flex-col items-end gap-1.5">
        {idea.piece ? (
          <>
            <span className={chipClass(idea.piece.status)}>{stageLabel(idea.piece.status)}</span>
            {/* A set-aside, failed or taken-down piece leaves the subject open: offer it again. */}
            {!idea.written && canDraft && <Link to={startIdea(path, idea.label)} className={btnPrimary}>Start again</Link>}
            <Link to={pieceHref(path, idea.piece.id)} className={`font-ui text-[13px] text-amber-glow underline underline-offset-2 hover:text-paper ${focus}`}>Open the piece</Link>
          </>
        ) : canDraft && (
          <Link to={startIdea(path, idea.label)} className={btnPrimary}>Start a piece</Link>
        )}
        {hasSearchDemand(idea) && (
          <button type="button" className={btn} disabled={hiding} onClick={() => onHide(idea)}>Not our hobby</button>
        )}
      </div>
    </li>
  )
}

function PieceRow({ p }: { p: SearchPiece }) {
  return (
    <li className="flex flex-wrap items-baseline gap-x-4 gap-y-1 font-ui text-[13px] text-paper-dim">
      {p.url
        ? <a href={p.url} target="_blank" rel="noopener noreferrer" className={`text-[14px] font-semibold text-amber-glow underline underline-offset-2 hover:text-paper ${focus}`}>{p.title}</a>
        : <span className="text-[14px] font-semibold text-paper">{p.title}</span>}
      {p.published_at && <span>{etDate(p.published_at)}</span>}
      {p.phrase && <span>“{p.phrase}”</span>}
      {p.counted ? (
        <span>
          {p.clicks} {p.clicks === 1 ? 'click' : 'clicks'} · {p.impressions} impressions
          {p.avg_position != null && <> · position {p.avg_position.toFixed(1)}</>}
        </span>
      ) : <span className="text-paper-faint">{p.free_version ? 'members only, free version listed' : 'members only, not counted'}</span>}
    </li>
  )
}

function SearchResultsPanel({ slug, searchOn }: { slug: string; searchOn: boolean }) {
  const q = useQuery({ queryKey: ['editorial', 'search-results', slug], queryFn: () => getSearchResults(slug, 28), retry: false })
  if (!showResultsPanel(searchOn, q.data) || !q.data) return null
  const r = q.data
  return (
    <section aria-label="Results from search ideas" className={`${card} flex flex-col gap-3 p-[18px]`}>
      <h2 className="m-0 font-serif text-[20px] font-medium text-paper">{resultsHeadline(r)}</h2>
      {r.pieces.length > 0 && <ul className="m-0 flex list-none flex-col gap-2 p-0">{r.pieces.map((p) => <PieceRow key={p.draft_id} p={p} />)}</ul>}
      <p className="m-0 font-ui text-[13px] text-paper-dim">Other ideas: {r.others.published} published · {r.others.clicks} Google clicks</p>
      <p className="m-0 font-ui text-[12px] text-paper-faint">{SETTLE_NOTE}</p>
    </section>
  )
}

export function IdeasPage() {
  const { section, publicationSlug } = useEditorialTitle()
  const canDraft = useCanDraft()
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  // The switch rides on the desk query the layout already caches.
  const desk = useQuery({ queryKey: ['editorial-desk', publicationSlug], queryFn: () => getDesk(publicationSlug), staleTime: 30_000, retry: false, refetchInterval: (query) => deskPollMs(query.state.data) })
  const searchOn = desk.data?.search_ideas_on === true
  const [hideError, setHideError] = useState<string | null>(null)
  const hide = useMutation({
    mutationFn: (idea: Idea) => notOurHobby(publicationSlug, idea.label),
    onSuccess: () => { setHideError(null); void qc.invalidateQueries({ queryKey: ['editorial', 'ideas', publicationSlug] }) },
    onError: (e) => {
      // The card is gone (404) or never had a search signal (409): just show the list as it is now.
      if (e instanceof ApiError && (e.status === 404 || e.status === 409)) { setHideError(null); void qc.invalidateQueries({ queryKey: ['editorial', 'ideas', publicationSlug] }); return }
      setHideError(describeError(e))
    },
  })
  const onHide = (idea: Idea) => {
    if (!window.confirm(`Hide this and stop suggesting '${phraseOf(idea)}'?`)) return
    hide.mutate(idea)
  }
  const show = parseShow(params.get('show'))
  const [own, setOwn] = useState('')
  const [ask, setAsk] = useState('')
  const [refresh, setRefresh] = useState<{ state: RefreshState; baseline: string | null; startedAt: number }>({ state: 'idle', baseline: null, startedAt: 0 })

  const q = useQuery({
    queryKey: ['editorial', 'ideas', publicationSlug, show],
    queryFn: () => getIdeas(publicationSlug, show),
    refetchInterval: (query) => {
      if (refresh.state !== 'gathering' && refresh.state !== 'already_running') return false
      return shouldPoll(refresh.baseline, query.state.data?.computed_at, refresh.startedAt, Date.now()) ? POLL_MS : false
    },
  })
  const data = q.data
  const computedAt = data?.computed_at ?? null

  // Settle the refresh state once the stamp moves or time runs out.
  const timer = useRef<number | null>(null)
  useEffect(() => {
    if (refresh.state !== 'gathering' && refresh.state !== 'already_running') return
    if (computedAt && computedAt !== refresh.baseline) return
    const left = refresh.startedAt + 5 * 60_000 - Date.now()
    timer.current = window.setTimeout(() => setRefresh((r) => ({ ...r, state: 'gave_up' })), Math.max(left, 0) + 1000)
    return () => { if (timer.current) window.clearTimeout(timer.current) }
  }, [refresh.state, refresh.baseline, refresh.startedAt, computedAt])

  const start = useMutation({
    mutationFn: () => refreshIdeas(publicationSlug),
    onSuccess: (res) => setRefresh({ state: res.already_running ? 'already_running' : 'gathering', baseline: computedAt, startedAt: Date.now() }),
  })

  const setShow = (key: string) => {
    const next = new URLSearchParams(params)
    if (key === 'all') next.delete('show'); else next.set('show', key)
    setParams(next, { replace: true })
  }

  const submitOwn = (e: React.FormEvent) => {
    e.preventDefault()
    if (!own.trim()) return
    navigate(startLink(section.path, { label: own, ask }))
  }

  const refreshed = !!computedAt && computedAt !== refresh.baseline
  const msg = refreshMessage(refreshed ? 'idle' : refresh.state)
  const filters = filterCounts(data?.counts)

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1.5">
          <span className="label">{section.label} · Ideas</span>
          <h1 className="m-0 font-serif text-[40px] font-medium leading-[1.05] tracking-tight text-paper">What readers want to read</h1>
          <p className="m-0 max-w-[640px] font-ui text-[14px] text-paper-dim">
            Ranked from what people search for, what is trending, upcoming anniversaries, and how much the archive already holds on each subject.
          </p>
        </div>
        <span className="flex items-center gap-3 font-ui text-[13px] text-paper-faint">
          {computedAt && <span>Updated {etDate(computedAt)}, {etTime(computedAt)}</span>}
          <button type="button" className={btn} disabled={start.isPending} onClick={() => start.mutate()}>Refresh</button>
        </span>
      </header>
      {(msg || start.isError) && (
        <p role="status" className="m-0 font-ui text-[13px] text-paper-dim">{msg ?? describeError(start.error)}</p>
      )}

      {canDraft && <form onSubmit={submitOwn} className={`${card} flex flex-wrap items-end gap-3 p-[18px]`}>
        <label className="flex flex-[2_1_280px] flex-col gap-1.5 font-ui text-[14px] font-semibold text-paper">
          Your own idea
          <input className={field} type="text" value={own} onChange={(e) => setOwn(e.target.value)} placeholder="e.g. Grumman F7F Tigercat" />
        </label>
        <label className="flex flex-[3_1_320px] flex-col gap-1.5 font-ui text-[14px] font-semibold text-paper">
          <span>What should it answer? <span className="text-[12px] font-normal text-paper-faint">Optional</span></span>
          <input className={field} type="text" value={ask} onChange={(e) => setAsk(e.target.value)} placeholder="e.g. why it arrived too late for the war" />
        </label>
        <button type="submit" className={`${btnPrimary} min-h-11 disabled:opacity-50`} disabled={!own.trim()}>Start a piece</button>
      </form>}

      <RefreshPagesSection slug={publicationSlug} path={section.path} canPress={canDraft} desk={desk.data} />
      <SearchResultsPanel slug={publicationSlug} searchOn={searchOn} />
      {hideError && <p role="alert" className="m-0 font-ui text-[13px] text-paper-dim">{hideError}</p>}

      <div role="group" aria-label="Show ideas" className="flex flex-wrap gap-2">
        {filters.map((f) => (
          <button key={f.key} type="button" aria-pressed={show === f.key} onClick={() => setShow(f.key)}
            className={`${btn} ${show === f.key ? 'border-paper bg-paper text-ink-900 hover:bg-paper' : ''}`}>
            {f.text}
          </button>
        ))}
      </div>

      {q.isLoading ? (
        <p className="m-0 font-ui text-[14px] text-paper-faint">Loading…</p>
      ) : q.isError ? (
        <p role="alert" className="m-0 font-ui text-[14px] text-paper-dim">{IDEAS_UNAVAILABLE}</p>
      ) : data?.error ? (
        <p role="alert" className="m-0 font-ui text-[14px] text-paper-dim">{IDEAS_UNAVAILABLE}</p>
      ) : !data?.items.length ? (
        <p className="m-0 font-ui text-[14px] text-paper-dim">{IDEAS_EMPTY}</p>
      ) : (
        <ol className="m-0 flex list-none flex-col gap-2.5 p-0">
          {data.items.map((i) => <IdeaCard key={`${i.rank}-${i.label}`} idea={i} path={section.path} onHide={onHide} hiding={hide.isPending} />)}
        </ol>
      )}
      <p className="m-0 font-ui text-[12px] text-paper-faint">Hover any reason to see the evidence: the searches, the Reddit thread, the anniversary date.</p>
    </div>
  )
}
